import type { PresetKey } from '@/components/shared/vaultPresets'
import { BIGGER_VAULT_TVL } from '@/config/query/queryParams'
import { SELECTED_TOKENS_KEY, TVL_FROM_BOUND_KEY } from '@/constants/state'
import type { Vault } from '@/types/vault'
import { getAvailableTokens } from '@/utils/tokens/getAvailableTokens'
import { useCallback, useMemo } from 'react'
import { getGroupedTokens } from '../utils/tokens/getGroupedTokens'
import { type SearchStateEntries, useSearchState } from './useSearchState'

type FilterPreset = {
  key: PresetKey
  entries: SearchStateEntries
}

interface Props {
  vaults: Vault[]
}

export const useFilterPresets = ({ vaults }: Props) => {
  const { searchState, updateSearchState } = useSearchState({})
  const availableTokens = useMemo(() => getAvailableTokens(vaults), [vaults])
  const { ethTokens, usdCoins } = useMemo(() => getGroupedTokens(availableTokens), [availableTokens])
  const presets = useMemo<FilterPreset[]>(
    () => [
      { key: 'all', entries: [] },
      { key: 'tvl', entries: [[TVL_FROM_BOUND_KEY, BIGGER_VAULT_TVL]] },
      { key: 'eth', entries: [[SELECTED_TOKENS_KEY, ethTokens]] },
      { key: 'usd', entries: [[SELECTED_TOKENS_KEY, usdCoins]] },
    ],
    [ethTokens, usdCoins]
  )

  const findActivePreset = useCallback(
    (searchState: URLSearchParams) => {
      for (let i = presets.length - 1; i >= 0; i--) {
        const $isActive = presets[i].entries.every(
          ([key, value]) =>
            searchState.getAll(key).sort().toString() ===
            (Array.isArray(value) ? value.sort().toString() : value.toString())
        )
        if ($isActive) return presets[i].key
      }
    },
    [presets]
  )

  const handlePresetClick = useCallback(
    (key: PresetKey) => {
      const entries = presets.find((preset) => preset.key === key)?.entries ?? presets[0].entries
      const clearEntries: SearchStateEntries = Array.from(searchState.keys()).map((searchKey) => [searchKey, undefined])

      updateSearchState(...clearEntries, ...entries)
    },
    [presets, searchState, updateSearchState]
  )

  return useMemo(() => {
    const activePreset = findActivePreset(searchState) ?? presets[0].key
    return { activePreset, handlePresetClick }
  }, [searchState, presets, findActivePreset, handlePresetClick])
}
